import React, {useEffect, useState} from "react";
import Grid from "@mui/material/Unstable_Grid2";
import UserAddressesForm from "./userAddressesForm";
import {ContactAddressDto} from "../../../features/contact/contact-api";
import {useAppSelector} from "../../../store";
import {PageFragments} from "../pages";
import {
  AddressCardValues,
  ContactAddressValues,
  ContactCategory,
  PickupLocationValues,
} from "../../../Delivery/DeliveryAddress/components/interfaces";
import {getLangCodeFromI18n} from "../../../app/app.component";
import {getLocalizedToponyms} from "../../../ParcelShipments/AllParcelShipments/utils/parcelShipmentTable.utils";
import useGetUserContact from "../../../common/hooks/useGetUserContact";
import {getCustomValue} from "../../../../utils/helper.utils";
import useGetUserAddresses, {
  UserAddressType,
} from "../../../common/hooks/useGetUserAddresses";

export type UserContactAddressInfo = ContactAddressValues &
  PickupLocationValues & {
    contactId?: number | null;
    contactAddressId?: number | null;
    addressLine?: string | null;
    isPickupLocation?: boolean | null;
    contactCustomValues?: any;
    countryCustomValues?: any;
  };

interface AccountSavedAddressesFormProps {
  onChangePageState: (pageState: PageFragments) => void;
  triggerHandleBack?: boolean;
  triggerContactsRefetch?: boolean;
}

const CONTACT_ADDRESS_INITIAL_VALUES: UserContactAddressInfo = {
  toAddressFirstName: "",
  toAddressLastName: "",
  toAddressPhoneNumber: "",
  toAddressEmail: "",
  toAddressCountryCode: "",
  toAddressCountryName: "",
  toAddressRegionCode: "",
  toAddressRegionName: "",
  toAddressDistrict: "",
  toAddressCity: "",
  toAddressPostalCode: "",
  toAddressStreetName: "",
  toAddressHouseNumber: "",
  toAddressApartment: "",
  toAddressSetAsDefault: false,
  toAddressContactCategory: ContactCategory.Individual,
  toAddressCompanyName: "",
  pickupLocationFirstName: "",
  pickupLocationLastName: "",
  pickupLocationPhoneNumber: "",
  pickupLocationEmail: "",
  pickupLocationCountryCode: "",
  pickupLocationCountryName: "",
  pickupLocationDistrict: "",
  pickupLocationContactAddressId: null,
  pickupLocationPostOffices: true,
  pickupLocationParcelLockers: true,
  pickupLocationAddressLine: "",
  pickupLocationContactCategory: ContactCategory.Individual,
  pickupLocationCompanyName: "",
  contactId: null,
  contactAddressId: null,
  addressLine: "",
  isPickupLocation: null,
};

export default function AccountSavedAddressesForm({
  onChangePageState,
  triggerHandleBack = undefined,
  triggerContactsRefetch = undefined,
}: AccountSavedAddressesFormProps) {
  const lang = getLangCodeFromI18n();
  const userState = useAppSelector((state) => state.userState);

  const [contactAddresses, setContactAddresses] =
    useState<(UserContactAddressInfo & AddressCardValues)[] | null>(null);
  const [editingWidth, setEditingWidth] = useState<boolean>(false);
  const [defaultContactAddressId, setDefaultContactAddressId] =
    useState<number>(0);

  const {getDeliveryAddresses} = useGetUserAddresses({
    addressType: UserAddressType.All,
  });

  const {getContact} = useGetUserContact();

  const [triggerUpdateAddresses, setTriggerUpdateAddresses] =
    useState<boolean>(false);
  const handleUpdateAddresses = () => {
    setTriggerUpdateAddresses((prevState) => !prevState);
  };

  const mapContactsToFormValues = (
    contactAddresses: ContactAddressDto[],
  ): UserContactAddressInfo[] => {
    const userContactAddresses: UserContactAddressInfo[] = [];
    if (contactAddresses?.length) {
      for (const contactAddress of contactAddresses) {
        const [
          localizedCountryName,
          localizedStateName,
          localizedCityName,
          localizedAddressName,
        ] = getLocalizedToponyms(contactAddress, lang);

        userContactAddresses.push({
          ...CONTACT_ADDRESS_INITIAL_VALUES,
          ...{
            name:
              contactAddress?.contact?.customValues?.contactCategory ===
              ContactCategory.Business
                ? contactAddress?.contact?.name
                : `${contactAddress.contact?.contactFirstName} ${contactAddress.contact?.contactLastName}`,
            contactName: `${contactAddress.contact?.contactFirstName} ${contactAddress.contact?.contactLastName}`,
            addressLine1: `${localizedAddressName}`,
            addressLine2: `${contactAddress.addressLine2 ?? ""}`,
            houseNumber: `${
              contactAddress.customValues?.toAddressHouseNumber ?? ""
            }`,
            city: localizedCityName,
            region: localizedStateName,
            postalCode: contactAddress.postalCode,
            country: localizedCountryName,
            phoneNumber: contactAddress?.contact?.phoneNumber,
          },
          ...(contactAddress.customValues?.is_pickup_location
            ? {
                pickupLocationFirstName:
                  contactAddress?.contact?.contactFirstName || "",
                pickupLocationLastName:
                  contactAddress?.contact?.contactLastName || "",
                pickupLocationPhoneNumber:
                  contactAddress?.contact?.phoneNumber || "",
                pickupLocationEmail:
                  contactAddress?.contact?.emailAddress ?? "",
                pickupLocationCountryCode: contactAddress?.countryCode || "",
                pickupLocationCountryName: localizedCountryName || "",
                pickupLocationContactAddressId:
                  contactAddress?.contactAddressId || null,
                pickupLocationPostOffices: true,
                pickupLocationParcelLockers: true,
                pickupLocationAddressLine: localizedAddressName || "",
                contactId: contactAddress?.contactId,
                contactAddressId: contactAddress?.contactAddressId,
                addressLine: localizedAddressName || "",
                isPickupLocation: true,
                pickupLocationContactCategory: contactAddress?.contact
                  ?.customValues?.contactCategory as ContactCategory,
                pickupLocationCompanyName: contactAddress?.contact?.name ?? "",
              }
            : {
                toAddressFirstName:
                  contactAddress?.contact?.contactFirstName || "",
                toAddressLastName:
                  contactAddress?.contact?.contactLastName || "",
                toAddressPhoneNumber:
                  contactAddress?.contact?.phoneNumber || "",
                toAddressEmail: contactAddress?.contact?.emailAddress || "",
                toAddressCountryCode: contactAddress?.countryCode || "",
                toAddressCountryName: localizedCountryName || "",
                toAddressRegionCode: contactAddress?.stateCode || "",
                toAddressRegionName: localizedStateName || "",
                toAddressDistrict:
                  contactAddress?.customValues?.toAddressDistrict || "",
                toAddressCity: contactAddress?.cityName || "",
                toAddressPostalCode: contactAddress?.postalCode || "",
                toAddressStreetName: contactAddress?.addressLine || "",
                toAddressHouseNumber:
                  contactAddress?.customValues?.toAddressHouseNumber || "",
                toAddressApartment: contactAddress?.addressLine2 || "",
                toAddressSetAsDefault: false,
                contactId: contactAddress?.contactId,
                contactAddressId: contactAddress?.contactAddressId,
                addressLine: contactAddress?.addressLine || "",
                isPickupLocation: false,
                toAddressContactCategory: contactAddress?.contact?.customValues
                  ?.contactCategory as ContactCategory,
                toAddressCompanyName: contactAddress?.contact?.name ?? "",
              }),
        });
      }
    }

    return userContactAddresses;
  };

  useEffect(() => {
    if (userState?.contactId) {
      getContact().then((contact: any) => {
        const responseDefaultContactAddressId = Number(
          getCustomValue(
            contact?.data?.customValues,
            "default_contact_address_ID",
          ),
        );

        if (!isNaN(responseDefaultContactAddressId)) {
          setDefaultContactAddressId(responseDefaultContactAddressId);
        }
      });
    }
    getDeliveryAddresses().then((result) => {
      setContactAddresses(mapContactsToFormValues(result?.items));
    });
  }, [
    userState?.contactId,
    triggerContactsRefetch,
    triggerUpdateAddresses,
    lang,
  ]);

  return (
    <Grid container columns={{xs: 6, md: 12}}>
      <Grid lg={12} xl={editingWidth ? 9 : 12}>
        <UserAddressesForm
          defaultContactAddressId={defaultContactAddressId}
          setDefaultContactAddressId={setDefaultContactAddressId}
          contactAddresses={contactAddresses}
          onChangePageState={onChangePageState}
          triggerHandleBack={triggerHandleBack}
          updateAddresses={handleUpdateAddresses}
          setEditingWidth={setEditingWidth}
        />
      </Grid>
    </Grid>
  );
}
